import { Operator, Pattern } from './models/Pattern';
import { fromLegacyPalette } from './palette';

/**
 * Keeps an analysis (patterns, labels, descriptions, colours, default operators) per piece in the browser and as a
 * file (audit ISSUE-003: a reload or a switch of the piece discarded all work).
 */

export interface SavedAnalysis {
  app: 'MelodyVis';
  version: 1;
  sheet: { title: string; composer: string };
  savedAt: string;
  patterns: Pattern[];
  defaultOperators: Operator[];
  /** Saved with the Figure 2 colours; analyses without it have the earlier palette's colours. */
  palette?: typeof currentPalette;
  /** The names the analyst gave discovered families, by their discovery label. */
  names?: Record<string, string>;
}

export const currentPalette = 'figure2';

/**
 * Own patterns of an analysis saved with the earlier palette take the Figure 2 colours (colours the analyst chose
 * stay). Shown families are left alone: they follow the current discovery (App).
 */
export const withCurrentPalette = (analysis: SavedAnalysis): SavedAnalysis =>
  analysis.palette === currentPalette
    ? analysis
    : {
        ...analysis,
        palette: currentPalette,
        patterns: analysis.patterns.map((pattern) =>
          pattern.id.startsWith('family-')
            ? pattern
            : { ...pattern, color: fromLegacyPalette(pattern.color) }
        )
      };

const keyFor = (sheet: { title: string; composer: string }) =>
  `melodyvis.analysis.v1:${sheet.title}|${sheet.composer}`;

export const readAnalysis = (sheet: {
  title: string;
  composer: string;
}): SavedAnalysis | undefined => {
  try {
    const stored = window.localStorage.getItem(keyFor(sheet));
    return stored ? parseAnalysis(stored) : undefined;
  } catch {
    return undefined;
  }
};

export const writeAnalysis = (analysis: SavedAnalysis) => {
  try {
    const hasNames = Object.keys(analysis.names ?? {}).length > 0;
    if (analysis.patterns.length === 0 && !hasNames)
      window.localStorage.removeItem(keyFor(analysis.sheet));
    else window.localStorage.setItem(keyFor(analysis.sheet), JSON.stringify(analysis));
  } catch (error) {
    // Storage can be full or blocked (private windows); the analysis then lives in memory as before.
    console.warn('MelodyVis: the analysis could not be saved in the browser.', error);
  }
};

const isOccurrence = (value: unknown) =>
  typeof value === 'object' &&
  value !== null &&
  ['firstOffset', 'lastOffset', 'voiceNumber'].every(
    (key) => typeof (value as Record<string, unknown>)[key] === 'number'
  );

const isPattern = (value: unknown): value is Pattern => {
  const pattern = value as Pattern;
  return (
    typeof pattern === 'object' &&
    pattern !== null &&
    typeof pattern.id === 'string' &&
    typeof pattern.label === 'string' &&
    Array.isArray(pattern.occurrences) &&
    pattern.occurrences.every(isOccurrence) &&
    Array.isArray(pattern.connectedToByOperator) &&
    Array.isArray(pattern.connectedToByOccurrences)
  );
};

/** The analysis in a JSON text, or an exception with a readable reason. */
export const parseAnalysis = (text: string): SavedAnalysis => {
  const data = JSON.parse(text);
  if (data?.app !== 'MelodyVis' || data?.version !== 1)
    throw new Error('This is not a MelodyVis analysis file.');
  if (!Array.isArray(data.patterns) || !data.patterns.every(isPattern))
    throw new Error('The analysis file contains no valid patterns.');
  // Names are optional; anything but text is left out.
  const names = Object.fromEntries(
    Object.entries(typeof data.names === 'object' && data.names !== null ? data.names : {}).filter(
      ([, name]) => typeof name === 'string'
    )
  ) as Record<string, string>;
  return { ...data, names } as SavedAnalysis;
};

export const analysisFileName = (sheet: { title: string }) =>
  `${sheet.title.replace(/[\\/:*?"<>|]+/g, '_')} - MelodyVis analysis.json`;
